<template>
  <div class="fixed-bottom mx-auto d-flex justify-content-center">
    <div class="page-nav d-flex justify-content-between align-items-center">
        <div class="back">
            <button ref="backButton" v-if="showBack" type="button" class="btn-secondary btn d-block d-sm-inline-block p-4 border-end" :class="{ 'disabled': disable }" :href="getBackLink"
                @click="onBackClicked">
                <i class="bi bi-arrow-left"></i>
                Back
            </button>

            <div v-if="showCustom" class="col-6">
                <button type="button" class="btn-back btn d-block d-sm-inline-block" :class="{ 'disabled': disable }" :href="customLink">
                    {{ customText }}
                </button>
            </div>

            <div v-if="!showBack && !showCustom" class="d-block d-sm-inline-block p-4">
              <span class="invisible">Spacer</span>
            </div>
        </div>
        <div class="page-nav-number text-center">
            <p class="m-0">{{ currentWorkflowStep?.number ?? '' }} of {{ workflowStepsCount }}</p>
            <p v-if="nextWorkflowStep != null" class="m-0">Next: {{ nextWorkflowStep?.text ?? '' }}</p>
        </div>
        <div class="next">
            <button ref="nextButton" v-if="showNext" class="btn-success btn d-block d-sm-inline-block skin-nav-button p-4 border-start" type="button" :class="{ 'disabled': disable }"
                :href="getNextLink" @click="onNextClicked($event)">
                {{ nextText }}
                <i class="bi bi-arrow-right"></i>
            </button>
        </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from "vue";
import { workflow } from "@/guiide/ts/workflow/workflow";
import { WorkflowStep } from "@/guiide/ts/workflow/workflowStep";
import { useStore } from "vuex";

const store = useStore();

defineExpose({ takeFocus });
const emit = defineEmits(['nextClicked']);

const props = defineProps({
  backLink: { type: String, required: false },
  nextLink: { type: String, required: false },
  nextText: { type: String, required: false, default: "Next" },
  customLink: { type: String, required: false },
  customText: { type: String, required: false },
  showBack: { type: Boolean, default: true },
  showNext: { type: Boolean, default: true },
  showCustom: { type: Boolean, required: false, default: false },
  parentWillHandleNext: { type: Boolean, required: false, default: false }
});

const currentWorkflowStep = ref<WorkflowStep>(null);
const nextWorkflowStep = ref<WorkflowStep>(null);
const workflowStepsCount = ref<number>(0);
const backButton = ref(null);
const nextButton = ref(null);

onMounted(async () => {
  currentWorkflowStep.value = await workflow.getCurrentStep();
  nextWorkflowStep.value = await workflow.getNextStep();
  workflowStepsCount.value = (await workflow.getSteps()).length;
})

const getBackLink = computed<string>(() => {
  return `/${props.backLink}`;
});

const getNextLink = computed<string>(() => {
  return `/${props.nextLink}`;
});

const disable = computed<boolean>(() => {
  return store.state.transactionOngoing;
});

function onBackClicked(event) {
  event.preventDefault();
  workflow.goToPreviousWorkflowStep();
}

function onNextClicked(event) {
  event.preventDefault();

  if (!props.parentWillHandleNext && (props.nextLink?.length ?? 0) > 0) {
    window.location.href = props.nextLink;
    return;
  }

  emit("nextClicked");
}

function takeFocus(key: string): boolean {
  if (key == "back" && props.showBack) {
    backButton.value.focus();
    return true;
  }

  if (key == "next" && props.showNext) {
    nextButton.value.focus();
    return true;
  }

  return false;
}
</script>

<style scoped>
.page-nav {
    width: 100%;
}

@media (min-width: 768px) {
    .page-nav {
        width: 500px;
        border-radius: 6px;
    }
}

@keyframes slide1 {
  0%,
  100% {
    transform: translate(0, 0);
  }

  50% {
    transform: translate(10px, 0);
  }
}

@keyframes slide2 {
    0%, 100% {
        transform: translate(0, 0);
    }

    50% {
        transform: translate(-10px, 0);
    }
}

a:hover {
  cursor: pointer;
}

@media (min-width: 768px) {
  .fixed-bottom {
    position: relative !important;
    border-top: none !important;
  }
}
</style>
